import styled from '@emotion/styled'
import { LEADERBOARD, Mediatype, SKIN_SINGLE_AD } from '@pubstack/common/src/adFormat'
import { DisplayedAdUnit, getAdUnitMediatypes, resolveInstreamPlaybackMethod } from '@pubstack/common/src/adunit'
import { Fragment, useState } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { Tooltip } from '~/components/Tooltip'
import { WithClassName } from '~/types/utils'
import { INSTREAM_PLCMT_OPTS, INSTREAM_POSITION_OPTS, INSTREAM_START_PLAYING_OPTS } from '../PureAdmAdUnitFirstStep'

const TooltipStyled = styled(Tooltip)`
  width: 18px;
  height: 18px;
`
const Title = styled.h2`
  ${Fonts.P1}
  font-weight: 500;
`

const MediatypesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const BannerAttributes = styled.div`
  padding: 8px 0 12px 34px;
  display: flex;
  gap: 14px;
  flex-direction: column;
  h3 {
    ${Fonts.P2}
    color: ${Colors.SlateGrey};
    font-weight: normal;
  }
`
const AlignedText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`
const AdUnitDetailWrapper = styled.div`
  display: flex;
  gap: 10px;
`
const NoValueText = styled.div`
  color: ${Colors.Ash};
  font-style: italic;
`
const InlineAdUnitDetailWrapper = styled(AdUnitDetailWrapper)`
  flex-direction: row;
  gap: 20px;
  & > ${AdUnitDetailWrapper} {
    width: 209px;
  }
`

const pipeSeparator = `\xa0 | \xa0`
export type AdUnitMediatypesDetailsProps = WithClassName & {
  adUnit: DisplayedAdUnit
}

const BaseAdUnitMediatypesDetails = ({ className, adUnit }: AdUnitMediatypesDetailsProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const adUnitMediatypes: Mediatype[] = getAdUnitMediatypes(adUnit)
  const hasSkin: boolean = adUnit.adFormat?.id === LEADERBOARD.id || adUnit.adFormat?.id === SKIN_SINGLE_AD.id
  return (
    <MediatypesWrapper className={className}>
      {adUnit.adFormat?.mediatypes.map((mediatype, i) => (
        <Fragment key={i}>
          <AlignedText>
            <Icon width={'26px'} name={adUnitMediatypes.includes(mediatype) ? 'check' : 'close'} fill={adUnitMediatypes.includes(mediatype) ? Colors.Success : Colors.Hurricane} />
            <span>{mediatype}</span>
          </AlignedText>
          {mediatype === 'Native' && adUnitMediatypes.includes(mediatype) && adUnit.nativeTemplate === '300x250' && (
            <AlignedText>
              <Icon name={'native_large_rectangle'} height={'36px'} width={'36px'} fill={Colors.Rose} />
              Large rectangle template
            </AlignedText>
          )}
          {mediatype === 'Banner' && adUnitMediatypes.includes(mediatype) && (
            <BannerAttributes>
              {hasSkin && (
                <AlignedText>
                  <Icon width={'26px'} name={adUnit.skinEnabled ? 'check' : 'close'} fill={adUnit.skinEnabled ? Colors.Success : Colors.Hurricane} />
                  <span>Skin</span>
                </AlignedText>
              )}
              <AlignedText>
                <Icon width={'26px'} name={adUnit.fluid ? 'check' : 'close'} fill={adUnit.fluid ? Colors.Success : Colors.Hurricane} />
                <span>Fluid</span>
              </AlignedText>
              <AdUnitDetailWrapper>
                <div>
                  <AlignedText>
                    <h3>Standard sizes</h3>
                    <TooltipStyled title={'Standard sizes (px) - ad server, header bidding'}>
                      <Icon width={'16px'} name={'info'} fill={Colors.Hurricane} />
                    </TooltipStyled>
                  </AlignedText>
                  <div>{adUnit.sizes && adUnit.sizes.length > 0 ? <span>{adUnit.sizes?.join(pipeSeparator)}</span> : <NoValueText>No standard sizes.</NoValueText>}</div>
                </div>
              </AdUnitDetailWrapper>
              <AdUnitDetailWrapper>
                <div>
                  <AlignedText>
                    <h3>Custom sizes</h3>
                    <TooltipStyled title={'Custom sizes (px) - ad server only'}>
                      <Icon width={'16px'} name={'info'} fill={Colors.Hurricane} />
                    </TooltipStyled>
                  </AlignedText>
                  <div>{adUnit.customSizes && adUnit.customSizes.length > 0 ? <span>{adUnit.customSizes?.join(pipeSeparator)}</span> : <NoValueText>No custom sizes.</NoValueText>}</div>
                </div>
              </AdUnitDetailWrapper>
            </BannerAttributes>
          )}
          {mediatype === 'Instream' && adUnit.instreamParams && adUnitMediatypes.includes('Instream') && (
            <BannerAttributes>
              <AdUnitDetailWrapper>
                <div>
                  <AlignedText>
                    <h3>Player size</h3>
                  </AlignedText>
                  <div>{adUnit.sizes && adUnit.sizes.length > 0 ? <span>{adUnit.sizes?.join(pipeSeparator)}</span> : <NoValueText>No standard sizes.</NoValueText>}</div>
                </div>
              </AdUnitDetailWrapper>
              <InlineAdUnitDetailWrapper>
                <AdUnitDetailWrapper>
                  <div>
                    <AlignedText>
                      <h3>Minimal ad duration</h3>
                    </AlignedText>
                    <div>
                      <span>{`${adUnit.instreamParams.mediaTypeParams.minduration} s`}</span>
                    </div>
                  </div>
                </AdUnitDetailWrapper>
                <AdUnitDetailWrapper>
                  <div>
                    <AlignedText>
                      <h3>Maximal ad duration</h3>
                    </AlignedText>
                    <div>
                      <span>{`${adUnit.instreamParams.mediaTypeParams.maxduration} s`}</span>
                    </div>
                  </div>
                </AdUnitDetailWrapper>
              </InlineAdUnitDetailWrapper>
              <Link
                label={'Advanced settings'}
                icon={isCollapsed ? 'chevron_up' : 'chevron_down'}
                trackingLabel={'adm-ad-units-instream-advanced-settings-set'}
                onClick={() => setIsCollapsed(!isCollapsed)}
              />
              {isCollapsed && (
                <>
                  <Title>Playing settings</Title>
                  <AdUnitDetailWrapper>
                    <div>
                      <AlignedText>
                        <h3>Start playing when</h3>
                      </AlignedText>
                      <div>
                        <span>{INSTREAM_START_PLAYING_OPTS.find((a) => a.value === (adUnit.instreamParams ? resolveInstreamPlaybackMethod(adUnit.instreamParams) : undefined))?.label}</span>
                      </div>
                    </div>
                  </AdUnitDetailWrapper>
                  <AdUnitDetailWrapper>
                    <div>
                      <AlignedText>
                        <h3>Position on screen (pos)</h3>
                      </AlignedText>
                      <div>
                        <span>{INSTREAM_POSITION_OPTS.find((a) => a.value === adUnit.instreamParams?.mediaTypeParams.pos)?.label}</span>
                      </div>
                    </div>
                  </AdUnitDetailWrapper>
                  <AdUnitDetailWrapper>
                    <div>
                      <AlignedText>
                        <h3>Allow skippable ads</h3>
                      </AlignedText>
                      <div>
                        <span>{adUnit.instreamParams.skippableads ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </AdUnitDetailWrapper>
                  <AdUnitDetailWrapper>
                    <div>
                      <AlignedText>
                        <h3>Placement (plcmt)</h3>
                      </AlignedText>
                      <div>
                        <span>{INSTREAM_PLCMT_OPTS.find((a) => a.value === adUnit.instreamParams?.mediaTypeParams.plcmt)?.label}</span>
                      </div>
                    </div>
                  </AdUnitDetailWrapper>
                  <Title>Video settings</Title>
                  <InlineAdUnitDetailWrapper>
                    <AdUnitDetailWrapper>
                      <div>
                        <AlignedText>
                          <h3>Minimal bit rate</h3>
                        </AlignedText>
                        <div>
                          <span>{`${adUnit.instreamParams.mediaTypeParams.minbitrate} kbps`}</span>
                        </div>
                      </div>
                    </AdUnitDetailWrapper>
                    <AdUnitDetailWrapper>
                      <div>
                        <AlignedText>
                          <h3>Maximal bit rate</h3>
                        </AlignedText>
                        <div>
                          <span>{`${adUnit.instreamParams.mediaTypeParams.maxbitrate} kbps`}</span>
                        </div>
                      </div>
                    </AdUnitDetailWrapper>
                  </InlineAdUnitDetailWrapper>
                  <AdUnitDetailWrapper>
                    <div>
                      <AlignedText>
                        <h3>Video protocols allowed</h3>
                      </AlignedText>
                      <div>
                        <span>{adUnit.instreamParams.protocols?.join(pipeSeparator)}</span>
                      </div>
                    </div>
                  </AdUnitDetailWrapper>
                </>
              )}
            </BannerAttributes>
          )}
        </Fragment>
      ))}
    </MediatypesWrapper>
  )
}

export const AdUnitMediatypesDetails = styled(BaseAdUnitMediatypesDetails)``
